import React, {ChangeEvent, useEffect, useMemo, useState} from "react"
import {useAppDispatch, useAppSelector} from "../../../../hooks/redux"
import {useDebounce} from 'usehooks-ts'
import useWindowSize from "../../../../hooks/useWindowSize"
import {fetchEmployees, setCurrentPage} from "../../../../store/actions/employeeActions"
import Button from "../../../generic/buttons/Button/Button"
import ModalEditEmployee from "../../modals/ModalEditEmployee/ModalEditEmployee"
import ModalSuccess from "../../modals/ModalSuccess/ModalSuccess"
import Loading from "../../Loading/Loading"
import Pagination from "../../../generic/navigation/Pagination"
import EmployeeLine from "../../Lines/EmployeeLine"
import {IDataTableTitle} from "../../../../models"
import Search from "../../Search"
import {IFetchEmployeesParams} from "../../../../models/employee"
import classes from "../Handbook.module.scss"

export default function Employees() {

  const dispatch = useAppDispatch()
  const windowSize = useWindowSize()
  const {list, isLoading, totalCount, pages, pageSize, fetchError, currentPage} = useAppSelector(state => state.employee)
  const [modalAddShown, modalAddToggle] = useState(false)
  const [modalAddSuccessShown, modalAddSuccessToggle] = useState(false)
  const [modalEditSuccessShown, modalEditSuccessToggle] = useState(false)
  const [modalDeleteSuccessShown, modalDeleteSuccessToggle] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebounce<string>(searchQuery, 500)

  const pageChangeHandler = ({selected}: {selected: number}) => {
    dispatch(setCurrentPage(selected + 1))
  }

  const changeSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const fetchParams = useMemo(() => {
    const obj: IFetchEmployeesParams = {
      page: currentPage,
      pageSize
    }
    if (debouncedSearchQuery) {
      obj.query = debouncedSearchQuery
      obj.pageSize = 0
      obj.page = 1
    }
    return obj
  }, [currentPage, pageSize, debouncedSearchQuery])

  useEffect(() => {
    dispatch(fetchEmployees(fetchParams))
  }, [dispatch, fetchParams])

  const titles: IDataTableTitle[] = [
    {
      id: 1,
      name: "ФИО сотрудника"
    },
    {
      id: 2,
      name: "Телефон"
    },
    {
      id: 3,
      name: "Роль"
    },
    {
      id: 4,
      name: "Бригада"
    },
    {
      id: 5,
      name: "Статус"
    },
    {
      id: 6,
      name: "",
      className: classes.data_cell_menu
    },
  ]

  const headerFieldsTemplate = titles.map((item) => {

    const cellCls = [classes.data_cell]

    if (item.className) {
      cellCls.push(item.className)
    }

    return (
      <div
        className={cellCls.join(" ")}
        key={item.id}
      >
        <span>{item.name}</span>
      </div>
    )
  })

  const employeeLines = useMemo(
    () => list.map(item => (
      <EmployeeLine
        key={item.id}
        item={item}
        editSuccess={modalEditSuccessToggle}
        deleteSuccess={modalDeleteSuccessToggle}
      />
    )),
    [list]
  )

  const dataTemplate = !isLoading && totalCount > 0 &&
    <div className={classes.data}>
      {windowSize.width >= 992 &&
        <div className={classes.data_header}>
          {headerFieldsTemplate}
        </div>
      }
      {employeeLines}
    </div>

  const emptyTemplate = !isLoading && totalCount === 0 && !fetchError &&
    <div className={classes.empty}>
      <div className={classes.empty_text}>
        {"Сотрудники не найдены"}
      </div>
    </div>

  const errorTemplate = fetchError &&
    <div className={classes.error}>
      <div className={classes.error_text}>
        {fetchError}
      </div>
    </div>

  const modalAddEmployee = modalAddShown &&
    <ModalEditEmployee
      close={() => modalAddToggle(false)}
      editSuccess={modalAddSuccessToggle}
    />

  const modalAddEmployeeSuccess = modalAddSuccessShown &&
    <ModalSuccess
      close={() => modalAddSuccessToggle(false)}
      title={"Сотрудник добавлен!"}
    />

  const modalUpdateSuccess = modalEditSuccessShown &&
    <ModalSuccess
      close={() => modalEditSuccessToggle(false)}
      title={"Сотрудник обновлен!"}
    />

  const modalDeleteSuccess = modalDeleteSuccessShown &&
    <ModalSuccess
      headerIconColor={"red"}
      close={() => modalDeleteSuccessToggle(false)}
      title={"Сотрудник удален!"}
    />

  return (
    <div className={classes.container}>
      {!fetchError && <div className={classes.top}>
        <div className={classes.search}>
          <Search
            value={searchQuery}
            onChange={changeSearchHandler}
          />
        </div>
        <div className={classes.button}>
          <Button
            color={"red"}
            className={classes.button_add}
            onClick={() => modalAddToggle(true)}
          >Добавить сотрудника</Button>
        </div>
      </div>}
      <div className={classes.content}>
        {isLoading && <Loading/>}
        {dataTemplate}
        {emptyTemplate}
        {errorTemplate}
      </div>
      <div className={classes.bottom}>
        <Pagination
          onPageChange={pageChangeHandler}
          pageRangeDisplayed={5}
          pageCount={pages}
          forcePage={currentPage - 1}
        />
      </div>
      {modalAddEmployee}
      {modalAddEmployeeSuccess}
      {modalUpdateSuccess}
      {modalDeleteSuccess}
    </div>
  )
}